import "./CustomStyling.css";
import { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { ChevronDown } from "lucide-react";

export const CustomSelect = ({ 
    label,
    value, 
    options, 
    onChange, 
    placeholder = "Select option",
    disabled = false,
    error = false,
    className = "",
    width = "100%",
    scrollToValue = null
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const ref = useRef(null);
    const dropdownRef = useRef(null);
    const selectRef = useRef(null);
    const [dropdownStyle, setDropdownStyle] = useState({});
    
    const selectedOption = options.find(opt => opt.value === value);

    const updatePosition = useCallback(() => {
        if (selectRef.current) {
            const rect = selectRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            const dropdownHeight = 200;
            const openAbove = spaceBelow < dropdownHeight && rect.top > dropdownHeight;

            setDropdownStyle({
                position: 'fixed',
                left: rect.left,
                width: rect.width,
                maxHeight: dropdownHeight,
                zIndex: 99999,
                ...(openAbove
                    ? { bottom: window.innerHeight - rect.top + 4, top: 'auto' }
                    : { top: rect.bottom + 4, bottom: 'auto' }),
            });
        }
    }, []);
    
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (ref.current && !ref.current.contains(e.target) && 
                dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Recalculate position on open & on scroll
    useEffect(() => {
        if (!isOpen) return;    
        const handleScrollOrResize = (e) => {
            if (e.type === 'resize') {
                updatePosition(); // recalculate on resize instead of closing
                return;
              }
            if (!e.target || !(e.target instanceof Node)) return;
            if (dropdownRef.current && dropdownRef.current.contains(e.target)) return;
            setIsOpen(false);
        };
        updatePosition(); 
        window.addEventListener('scroll', handleScrollOrResize, true);
        window.addEventListener('resize', handleScrollOrResize);
        return () => {
            window.removeEventListener('scroll', handleScrollOrResize, true);
            window.removeEventListener('resize', handleScrollOrResize);
        };
    }, [isOpen,updatePosition]);

    // Scroll to selected value
    useEffect(() => {
        if (isOpen && dropdownRef.current) {
            const targetValue = scrollToValue || value;
            if (targetValue) {
                const el = dropdownRef.current.querySelector(`[data-value="${targetValue}"]`);
                if (el) el.scrollIntoView({ block: 'center', behavior: 'instant' });
            }
        }
    }, [isOpen, value, scrollToValue]);

    const handleOpen = () => {
        if (disabled) return;
        if (isOpen) {
            setIsOpen(false);
            return;
        }
        // Calculate position BEFORE opening
        if (selectRef.current) {
            const rect = selectRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            const dropdownHeight = 200;
            const openAbove = spaceBelow < dropdownHeight && rect.top > dropdownHeight;
    
            setDropdownStyle({
                position: 'fixed',
                left: rect.left,
                width: rect.width,
                maxHeight: dropdownHeight,
                zIndex: 99999,
                ...(openAbove
                    ? { bottom: window.innerHeight - rect.top + 4, top: 'auto' }
                    : { top: rect.bottom + 4, bottom: 'auto' }),
            });
        }
        setIsOpen(true);
    };
    
    return (
        <div className={`ui-select-container ${className}`} ref={ref} style={{ width }}>
            {label && <label className="ui-label">{label}</label>}
            <div 
                ref={selectRef}
                className={`ui-select ${isOpen ? 'open' : ''} ${disabled ? 'disabled' : ''} ${error ? 'error' : ''}`}
                onClick={handleOpen}
            >
                <span className={selectedOption ? "ui-select-value" : "ui-select-placeholder"}>
                    {selectedOption?.label || placeholder}
                </span>
                <ChevronDown className={`ui-select-arrow ${isOpen ? 'open' : ''}`} size={18} />
            </div>
            {isOpen && createPortal(
                <div 
                    className="ui-select-dropdown" 
                    ref={dropdownRef} 
                    style={dropdownStyle}
                >
                    {options.map(opt => (
                        <div 
                            key={opt.value}
                            data-value={opt.value}
                            className={`ui-select-option ${value === opt.value ? 'selected' : ''}`}
                            onClick={() => { onChange(opt.value); setIsOpen(false); }}
                        >
                            {opt.label}
                        </div>
                    ))}
                </div>,
                document.body
            )}
        </div>
    );
};